import { useEffect, useRef, useState, type ReactNode } from 'react';
import './audio.css';

/*
 * O áudio da conversa. Modelo A (enxuto) do lote 4 do design system.
 *
 * É a mensagem que o vendedor mais recebe e a que menos consegue consumir: ele
 * está no salão, com cliente na frente. Toda decisão daqui sai disso — parado
 * mostra a DURAÇÃO (é o que decide se dá para ouvir agora), a velocidade só
 * existe enquanto toca, e o alvo de tocar chega a 44px no dedo.
 *
 * ── O que difere da entrega, e por quê ──────────────────────────────────
 *
 * A entrega supõe `duracao` e `picos` vindos do servidor. O contrato do anexo
 * não traz nenhum dos dois. Então `duracao` é opcional e, sem ela, o
 * componente lê a do próprio arquivo — e enquanto não sabe mostra `--:--` e
 * trava a barra, em vez de fingir. `picos` também é opcional: sem eles, o
 * trilho fino, como a entrega manda ("não desenhar o que não se sabe"). Quem
 * renderiza pode calcular a onda REAL no navegador depois do primeiro toque e
 * passá-la — é o que a conversa faz, via `aoTocar`.
 *
 * ── Por que não o `<audio controls>` do navegador ────────────────────────
 *
 * Ele vinha de graça com teclado e leitor de tela; trocá-lo por um desenho
 * nosso é assumir essa conta. Por isso a posição é um `<input type="range">`
 * de verdade, invisível sobre o desenho: responde a clique, arraste e teclado,
 * e dá o papel de slider a quem ouve a tela.
 */

export type FalhaAudio = { motivo: string; acao: string };

export type AudioProps = {
  /** O arquivo. Ausente = ainda não baixado. */
  src?: string;
  /**
   * Duração em segundos, vinda do ANEXO quando o servidor souber. Sem ela, o
   * componente lê a do arquivo — e até lá não finge que sabe.
   */
  duracao?: number;
  /** Picos 0–1 do áudio. Sem eles, o trilho. */
  picos?: number[] | null;
  /** De quem é o áudio, para o leitor de tela: "de Marcos Vilela". */
  rotulo?: string;
  /** Só no estado não baixado: "412 KB". */
  tamanho?: string;
  aoBaixar?: () => void;
  baixando?: boolean;
  falha?: FalhaAudio;
  /** O arquivo não abriu — o link assinado venceu, na maioria das vezes. */
  aoFalhar?: () => void;
  /** Chamado ao começar a tocar; é onde a tela pode ir buscar a onda real. */
  aoTocar?: () => void;
};

const VELOCIDADES = [1, 1.5, 2] as const;

/** 47 → "0:47". Sempre com dois dígitos no segundo. */
export function emMinutos(segundos: number): string {
  const seguro = Number.isFinite(segundos) && segundos > 0 ? Math.floor(segundos) : 0;
  return `${Math.floor(seguro / 60)}:${String(seguro % 60).padStart(2, '0')}`;
}

function Glifo({ nome }: { nome: 'play' | 'pausa' | 'baixar' }): ReactNode {
  return (
    <svg
      width={20}
      height={20}
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {nome === 'play' ? (
        <path d="M6.5 4.2 15 10l-8.5 5.8V4.2Z" fill="currentColor" stroke="none" />
      ) : null}
      {nome === 'pausa' ? (
        <path d="M7 4.5h2.2v11H7zM10.8 4.5H13v11h-2.2z" fill="currentColor" stroke="none" />
      ) : null}
      {nome === 'baixar' ? (
        <>
          <path d="M10 3.5v9" />
          <path d="m6.4 9.2 3.6 3.6 3.6-3.6" />
          <path d="M4 16.2h12" />
        </>
      ) : null}
    </svg>
  );
}

export function Audio({
  src,
  duracao,
  picos,
  rotulo,
  tamanho,
  aoBaixar,
  baixando = false,
  falha,
  aoFalhar,
  aoTocar,
}: AudioProps): ReactNode {
  const elemento = useRef<HTMLAudioElement>(null);
  const [tocando, definirTocando] = useState(false);
  const [posicao, definirPosicao] = useState(0);
  const [lida, definirLida] = useState<number | null>(null);
  const [velocidade, definirVelocidade] = useState<number>(1);

  useEffect(() => {
    if (elemento.current) elemento.current.playbackRate = velocidade;
  }, [velocidade]);

  /*
   * A duração pelo arquivo, quando o anexo não a traz.
   *
   * Alguns áudios de WhatsApp chegam sem cabeçalho de duração e o navegador
   * devolve `Infinity`; pular para o fim força o cálculo, e `timeupdate`
   * entrega o valor. Até lá, `--:--` — e não `0:00`, que é uma mentira curta.
   */
  const lerDuracao = (): void => {
    const audio = elemento.current;
    if (!audio) return;
    if (Number.isFinite(audio.duration) && audio.duration > 0) {
      definirLida(audio.duration);
      return;
    }
    const aoAtualizar = (): void => {
      if (!Number.isFinite(audio.duration)) return;
      definirLida(audio.duration);
      audio.currentTime = 0;
      audio.removeEventListener('timeupdate', aoAtualizar);
    };
    audio.addEventListener('timeupdate', aoAtualizar);
    audio.currentTime = 1e101;
  };

  const de = rotulo ? ` ${rotulo}` : '';

  if (falha) {
    return (
      <div className="audio audio--arquivo" role="status">
        <span className="audio__icone" aria-hidden="true">
          <Glifo nome="baixar" />
        </span>
        <span className="audio__corpo">
          <span className="audio__titulo">{falha.motivo}</span>
          <span className="audio__apoio">{falha.acao}</span>
        </span>
      </div>
    );
  }

  if (!src) {
    const titulo = duracao ? `Áudio de ${emMinutos(duracao)}` : 'Áudio';
    return (
      <button
        type="button"
        className="audio audio--arquivo"
        onClick={aoBaixar}
        disabled={baixando}
        aria-label={`Baixar ${titulo.toLowerCase()}${de}`}
      >
        <span className="audio__icone" aria-hidden="true">
          <Glifo nome="baixar" />
        </span>
        <span className="audio__corpo">
          <span className="audio__titulo">{titulo}</span>
          <span className="audio__apoio">
            {baixando ? 'Baixando…' : `Toque para baixar${tamanho ? ` · ${tamanho}` : ''}`}
          </span>
        </span>
      </button>
    );
  }

  const total = duracao ?? lida ?? 0;
  const proporcao = total > 0 ? Math.min(1, posicao / total) : 0;

  const irPara = (evento: { currentTarget: HTMLInputElement }): void => {
    const audio = elemento.current;
    const segundos = Number(evento.currentTarget.value);
    if (!audio || !Number.isFinite(segundos)) return;
    audio.currentTime = segundos;
    definirPosicao(segundos);
  };

  const faixa = (
    <input
      className="audio__faixa"
      type="range"
      min={0}
      max={total || 1}
      step="any"
      value={posicao}
      onChange={irPara}
      disabled={total === 0}
      aria-label="Posição do áudio"
      aria-valuetext={`${emMinutos(posicao)} de ${total === 0 ? 'duração desconhecida' : emMinutos(total)}`}
    />
  );

  return (
    <div className="audio">
      <audio
        ref={elemento}
        src={src}
        preload={duracao ? 'none' : 'metadata'}
        onLoadedMetadata={duracao ? undefined : lerDuracao}
        onDurationChange={duracao ? undefined : lerDuracao}
        onPlay={() => definirTocando(true)}
        onPause={() => definirTocando(false)}
        onTimeUpdate={(e) => definirPosicao(e.currentTarget.currentTime)}
        onEnded={() => {
          // Volta ao começo: um player parado no fim mostra o mesmo que um
          // que não tocou, e a pessoa não sabe qual dos dois está vendo.
          definirTocando(false);
          definirPosicao(0);
        }}
        onError={aoFalhar}
      />
      <button
        type="button"
        className="audio__tocar"
        /* O rótulo diz o que o clique FAZ, e muda junto do ícone: anunciar
           "tocar" num áudio já tocando manda parar sem avisar. */
        aria-label={`${tocando ? 'Pausar' : 'Tocar'} o áudio${de}`}
        onClick={() => {
          const audio = elemento.current;
          if (!audio) return;
          if (audio.paused) {
            aoTocar?.();
            void audio.play().catch(() => aoFalhar?.());
          } else {
            audio.pause();
          }
        }}
      >
        <Glifo nome={tocando ? 'pausa' : 'play'} />
      </button>

      {picos && picos.length > 0 ? (
        <span className="audio__onda">
          {picos.map((pico, indice) => (
            <span
              // O índice como chave, e aqui é o certo: a onda é uma lista de
              // alturas POSICIONAIS — a barra 7 é a sétima barra.
              key={indice}
              className={
                indice / picos.length < proporcao
                  ? 'audio__barra audio__barra--ouvida'
                  : 'audio__barra'
              }
              style={{ height: Math.max(3, Math.round(pico * 22)) }}
              aria-hidden="true"
            />
          ))}
          {faixa}
        </span>
      ) : (
        <span className="audio__trilho">
          <span
            className="audio__trilho-cheio"
            style={{ width: `${proporcao * 100}%` }}
            aria-hidden="true"
          />
          {faixa}
        </span>
      )}

      {/* Parado mostra a DURAÇÃO; tocando, o decorrido. */}
      <span className="audio__tempo">
        {total === 0 ? '--:--' : emMinutos(tocando || posicao > 0 ? posicao : total)}
      </span>

      {/* Só enquanto toca: parada, é um controle sem o que controlar. */}
      {tocando || posicao > 0 ? (
        <button
          type="button"
          className="audio__velocidade"
          aria-label={`Velocidade ${String(velocidade).replace('.', ',')}x. Tocar mais rápido`}
          onClick={() =>
            definirVelocidade(
              (v) => VELOCIDADES[(VELOCIDADES.indexOf(v as 1) + 1) % VELOCIDADES.length] ?? 1,
            )
          }
        >
          {String(velocidade).replace('.', ',')}×
        </button>
      ) : null}
    </div>
  );
}
